import { memo, useEffect } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import {
  getFluxmedDefaultCategoryLabel,
  type FluxmedDefaultTemplateCategory,
} from "../utils/messageTemplateUiUtils";
import {
  getFluxmedDefaultCategoryAccent,
  getFluxmedDefaultCategoryIcon,
} from "../utils/fluxmedDefaultTemplateCategoryIcons";

const CATEGORY_OPTIONS: Array<{
  id: FluxmedDefaultTemplateCategory | "all";
  label: string;
}> = [
    { id: "all", label: "Todos" },
    { id: "agendamento", label: getFluxmedDefaultCategoryLabel("agendamento") },
    { id: "cancelamento", label: getFluxmedDefaultCategoryLabel("cancelamento") },
    { id: "pagamento", label: getFluxmedDefaultCategoryLabel("pagamento") },
  ];

export interface MessageDefaultTemplatesCategoryFilterProps {
  selectedCategory: FluxmedDefaultTemplateCategory | "all";
  onChangeCategory: (category: FluxmedDefaultTemplateCategory | "all") => void;
}

const MessageDefaultTemplatesCategoryFilter = memo(
  ({ selectedCategory, onChangeCategory }: MessageDefaultTemplatesCategoryFilterProps) => {
    const theme = useTheme();

    useEffect(() => {
      if (selectedCategory === "outros") {
        onChangeCategory("all");
      }
    }, [onChangeCategory, selectedCategory]);

    return (
      <Stack spacing={0.75} sx={{ mt: 1.5, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Categoria
        </Typography>

        <Box
          sx={{
            display: "inline-flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            p: 1,
            maxWidth: "100%",
          }}
        >
          {CATEGORY_OPTIONS.map((option) => {
            const Icon = getFluxmedDefaultCategoryIcon(option.id);
            const isSelected = selectedCategory === option.id;
            const accent =
              option.id === "all"
                ? {
                  main: theme.palette.text.primary,
                  lightBg: alpha(theme.palette.primary.main, 0.1),
                  iconBg: theme.palette.primary.main,
                }
                : {
                  ...getFluxmedDefaultCategoryAccent(option.id, theme),
                  iconBg: getFluxmedDefaultCategoryAccent(option.id, theme).main,
                };

            return (
              <Box
                key={option.id}
                component="button"
                type="button"
                onClick={() => onChangeCategory(option.id)}
                aria-pressed={isSelected}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.625,
                  border: "none",
                  borderRadius: 1.5,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "0.8125rem",
                  lineHeight: 1.2,
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? accent.main : "text.secondary",
                  bgcolor: isSelected ? accent.lightBg : "transparent",
                  boxShadow: isSelected ? `inset 0 0 0 1px ${alpha(accent.main, 0.25)}` : "none",
                  transition: theme.transitions.create(
                    ["background-color", "color", "box-shadow"],
                    { duration: theme.transitions.duration.shortest },
                  ),
                  "&:hover": {
                    bgcolor: isSelected
                      ? accent.lightBg
                      : alpha(accent.main, 0.08),
                  },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: isSelected
                      ? accent.iconBg
                      : alpha(accent.main, 0.12),
                    color: isSelected ? theme.palette.common.white : accent.main,
                    transition: theme.transitions.create(["background-color", "color"], {
                      duration: theme.transitions.duration.shortest,
                    }),
                  }}
                >
                  <Icon sx={{ fontSize: 15 }} />
                </Box>
                {option.label}
              </Box>
            );
          })}
        </Box>
      </Stack>
    );
  },
);

MessageDefaultTemplatesCategoryFilter.displayName =
  "MessageDefaultTemplatesCategoryFilter";

export default MessageDefaultTemplatesCategoryFilter;
